import React, { createContext, useContext, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Sparkle, X } from "@phosphor-icons/react";
import { AISalesConversation } from "@/components/AISalesConversation";

const DockCtx = createContext({ open: () => {} });
export const useAISalesDock = () => useContext(DockCtx);

const CTX_MAP = [
  [/\/app\/(topup|services)/, "Send Top-up & airtime"],
  [/\/app\/esim/, "eSIM travel data"],
  [/\/app\/numbers/, "Global phone numbers"],
  [/\/app\/(pbx|business)/, "Cloud PBX / Business phone system"],
  [/\/app\/whatsapp/, "WhatsApp Business messaging"],
  [/\/app\/meet/, "iYeneh Meet video meetings"],
  [/\/app\/text/, "SMS messaging"],
  [/\/app\/(wallet|billing)/, "Wallet & funding"],
  [/\/app\/(products|more)/, "All iYeneh services"],
];

function pageContext(pathname) {
  for (const [re, label] of CTX_MAP) if (re.test(pathname)) return label;
  if (/\/app\/?$/.test(pathname)) return "Member dashboard";
  return null;
}

export function AISalesDockProvider({ children }) {
  const [isOpen, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const { pathname } = useLocation();
  const ctx = pageContext(pathname);

  const open = useCallback((prompt = null) => { setPrefill(prompt); setOpen(true); }, []);
  const close = useCallback(() => { setOpen(false); setPrefill(null); }, []);

  // Hide the dock on the dedicated full SI Sales workspace to avoid duplication.
  const onWorkspace = /\/app\/ai-sales/.test(pathname);
  // Home already has an inline "Ask iYeneh" CTA — hide the FAB there to avoid a duplicate button.
  const hideFab = onWorkspace || /^\/app\/?$/.test(pathname);

  return (
    <DockCtx.Provider value={{ open, close, isOpen }}>
      {children}

      {!hideFab && !isOpen && (
        <button data-testid="ai-sales-fab" onClick={() => open()}
          className="fixed bottom-24 right-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-xl shadow-primary/30 transition-transform hover:scale-105 md:bottom-6 md:right-6">
          <Sparkle size={20} weight="fill" /> Ask iYeneh
        </button>
      )}

      {!onWorkspace && isOpen && (
        <div className="fixed inset-0 z-[60]" data-testid="ai-sales-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkle size={20} weight="fill" /></div>
                <div>
                  <p className="font-display text-lg font-semibold leading-none">iYeneh Assistant</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{ctx ? `Helping with: ${ctx}` : "Your 24/7 SI — help, buy & get set up"}</p>
                </div>
              </div>
              <button data-testid="ai-sales-panel-close" onClick={close} className="rounded-full p-2 text-muted-foreground hover:bg-secondary"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-hidden px-5">
              <AISalesConversation context={ctx} autoSend={prefill} showRecommendations onNavigate={close} />
            </div>
          </div>
        </div>
      )}
    </DockCtx.Provider>
  );
}
